import React, { useEffect, useMemo, useRef, useState, useLayoutEffect, useCallback } from 'react';
import {
  authService,
  COLLAB_API_BASE,
  collabFetch,
  studentFetch,
  readEmbeddedLaunchParams,
  resolveAppSessionLaunch,
  bootstrapEmbeddedStudentSession,
  redeemTechywebTicket,
  formatPseudonym,
  isSessionGoneStatus,
  readSessionGoneReason,
  teacherSessionGoneNotice,
  teacherSessionNetworkNotice,
  saveSessionArtifact,
  artifactScopeFor,
  artifactSaveUserMessage,
  type User,
} from '@tl4k/frontend-shared';
import {
  JoinSession,
  AdminLogin,
  TL4KAppFooter,
  TL4KAppHeader,
  AvatarVisual,
  useAdminReturnTarget,
  parseAllowedOriginsEnv,
  useEmbeddedAllCaps,
  allCapsClassName,
  AllCapsStyles,
  EmbeddedSessionWaitingBanner,
  EmbeddedSessionNoticeBar,
  type SessionJoinData,
} from '@tl4k/admin-ui';
import { getDevPort } from '@tl4k/ports';
import { Maximize2, Minimize2, LogOut, Users } from 'lucide-react';
import { isTl4kEmbedded, postTl4kGoBack, embeddedInParent } from '../embedding';
// @ts-ignore
import Editor from './Editor.js';

const LS_KEY = 'tl4k_svg_edit_session_v1';

/** Retry del lookup della sessione derivata (standard TL4K_EMBEDDED_APP_BOOT). */
const SESSION_WAITING_RETRY_MS = 4000;

function collaboratePath(): boolean {
  if (typeof window === 'undefined') return false;
  const p = window.location.pathname;
  if (p === '/collaborate' || p.endsWith('/collaborate')) return true;
  const q = new URLSearchParams(window.location.search);
  return q.get('collaborate') === '1';
}


const TEACHER_JOIN_URL = window.location.origin
  ? `${window.location.origin}/src/editor/index.html?session_pin=`
  : '';

function withEmbeddedParam(pathname: string): string {
  if (!isTl4kEmbedded()) return pathname;
  const params = new URLSearchParams();
  params.set('tl4k-embedded', '1');
  return `${pathname}?${params.toString()}`;
}

function toSvgThumbnailDataUrl(svg: string): string {
  try {
    const encoded = btoa(unescape(encodeURIComponent(svg)));
    return `data:image/svg+xml;base64,${encoded}`;
  } catch {
    return '';
  }
}

export default function App() {
  const adminReturnAllowlist = useMemo(
    () => parseAllowedOriginsEnv(import.meta.env.VITE_ADMIN_RETURN_ALLOWED_ORIGINS as string | undefined),
    []
  );
  const adminReturnTarget = useAdminReturnTarget({ allowedOrigins: adminReturnAllowlist });

  const [view, setView] = useState<'join' | 'editor'>(() => (collaboratePath() ? 'join' : 'editor'));
  const [joinData, setJoinData] = useState<SessionJoinData | null>(null);

  const [teacherPin, setTeacherPin] = useState<string | null>(() => {
    const p = new URLSearchParams(window.location.search).get('session_pin');
    return p ? p.trim().toUpperCase() : null;
  });
  const [teacherUser, setTeacherUser] = useState<User | null>(null);
  const [teacherSessionId, setTeacherSessionId] = useState<string | null>(null);
  const [teacherSessionError, setTeacherSessionError] = useState<string | null>(null);
  // Avviso NON bloccante: sessione docente chiusa/scaduta, si lavora in locale.
  const [sessionNotice, setSessionNotice] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Studente embedded arrivato prima del docente: la sessione derivata non esiste ancora.
  const [sessionWaiting, setSessionWaiting] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [fullscreenActive, setFullscreenActive] = useState(false);
  const [labelsUppercase, setLabelsUppercase] = useEmbeddedAllCaps(false);
  const pin = joinData?.pin ?? teacherPin ?? '';
  const isTeacher = Boolean(teacherUser && teacherPin && !joinData && teacherSessionId);
  const isStandaloneStudent = Boolean(!teacherPin && joinData?.pin && joinData.pseudonymIdentityId);
  const isStandalone =
    !teacherPin && !joinData?.pin && !isTeacher;

  const [editorInstance, setEditorInstance] = useState<any>(null);
  const saveInFlightRef = useRef(false);
  const pendingSaveRef = useRef(false);
  const joinDataRef = useRef(joinData);
  const teacherSessionIdRef = useRef(teacherSessionId);
  const teacherPinRef = useRef(teacherPin);
  const teacherUserRef = useRef(teacherUser);
  const pinRef = useRef(pin);
  // Autosave: parte solo dopo editor.ready (`armed`), mai durante i caricamenti
  // programmatici (`suppress`) e mai in modalita' deep link ?artifact_id= (si
  // potrebbe sovrascrivere l'elaborato di un altro studente).
  const armedRef = useRef(false);
  const suppressRef = useRef(0);
  const deepLinkModeRef = useRef(
    Boolean(new URLSearchParams(window.location.search).get('artifact_id'))
  );
  // Modifiche fatte prima che esista una sessione (disegno in attesa del docente).
  const dirtyRef = useRef(false);
  const scheduleSaveRef = useRef<(() => void) | null>(null);

  // Stato e non useRef: il #container compare solo dopo "Caricamento…" (docente
  // con session_pin). Con un ref l'effetto che crea l'editor non ripartiva mai e
  // il docente vedeva un'area vuota dentro techyweb (aula 2026-10-01).
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);
  const shellRef = useRef<HTMLDivElement>(null);
  joinDataRef.current = joinData;
  teacherSessionIdRef.current = teacherSessionId;
  teacherPinRef.current = teacherPin;
  teacherUserRef.current = teacherUser;
  pinRef.current = pin;
  const isEmbedded = embeddedInParent();

  useEffect(() => {
    if (!isEmbedded) return;
    document.documentElement.classList.add('tl4k-embedded-shell');
    return () => document.documentElement.classList.remove('tl4k-embedded-shell');
  }, [isEmbedded]);

  const refreshUser = useCallback(() => {
    void authService
      .getCurrentUser()
      .then(setTeacherUser)
      .catch(() => setTeacherUser(null));
  }, []);

  useEffect(() => {
    const t = new URLSearchParams(window.location.search).get('auth_ticket');
    if (!t) return;
    void redeemTechywebTicket(t).then((ok) => {
      if (ok) {
        window.dispatchEvent(new CustomEvent('tl4k-auth-updated'));
        refreshUser();
      }
      const params = new URLSearchParams(window.location.search);
      params.delete('auth_ticket');
      const q = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (q ? `?${q}` : ''));
    });
  }, [refreshUser]);

  useEffect(() => {
    void authService
      .getCurrentUser()
      .then(setTeacherUser)
      .catch(() => setTeacherUser(null))
      .finally(() => setAuthLoading(false));
  }, []);

  useEffect(() => {
    const onAuth = () => refreshUser();
    window.addEventListener('tl4k-auth-updated', onAuth);
    return () => window.removeEventListener('tl4k-auth-updated', onAuth);
  }, [refreshUser]);

  useEffect(() => {
    const onFs = () => setFullscreenActive(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.get('session_pin')) return;
    params.delete('session_pin');
    const q = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (q ? `?${q}` : ''));
  }, []);

  useEffect(() => {
    if (collaboratePath() || teacherPin) return;
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (!raw) return;
      const o = JSON.parse(raw) as {
        pin?: string;
        sessionId?: string;
        pseudonymIdentityId?: string;
        participantId?: string;
        avatarConfig?: SessionJoinData['avatarConfig'];
        sessionName?: string;
      };
      if (o.pin && o.sessionId && o.pseudonymIdentityId) {
        setJoinData({
          pin: o.pin,
          sessionId: o.sessionId,
          pseudonymIdentityId: o.pseudonymIdentityId,
          participantId: o.participantId,
          avatarConfig: o.avatarConfig,
          sessionName: o.sessionName,
        });
        setView('editor');
      }
    } catch {
      /* ignore */
    }
  }, [teacherPin]);

  useEffect(() => {
    if (!joinData?.pin || !joinData.pseudonymIdentityId) return;
    try {
      localStorage.setItem(
        LS_KEY,
        JSON.stringify({
          pin: joinData.pin,
          sessionId: joinData.sessionId,
          pseudonymIdentityId: joinData.pseudonymIdentityId,
          participantId: joinData.participantId,
          avatarConfig: joinData.avatarConfig,
          sessionName: joinData.sessionName,
        })
      );
    } catch {
      /* ignore */
    }
  }, [joinData]);

  // Boot studente (standard docs/standards/TL4K_EMBEDDED_APP_BOOT.md): la sessione si
  // risolve con resolveAppSessionLaunch (supporta sia app_pin/derived_pin sia il solo
  // PIN incontro + lookup derivata). Se il docente non ha ancora aperto l'app il
  // lookup torna `waiting`: si riprova ogni 4 s e nel frattempo lo studente puo'
  // disegnare (il disegno viene salvato appena entra in sessione, vedi sotto).
  // Si entra SEMPRE con il PIN restituito dal resolver, mai con il PIN incontro
  // (readDerivedPseudonymParams accetterebbe tl4k_pin come PIN derivato).
  useEffect(() => {
    const search = window.location.search;
    const launch = readEmbeddedLaunchParams(search);
    if (launch.isTeacher || !launch.pseudonymId) return;
    if (!launch.appPin && !(launch.isEmbedded && launch.incontroPin)) return;
    const pseudonymId = launch.pseudonymId;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const retryLater = () => {
      if (!cancelled) timer = setTimeout(() => void attempt(), SESSION_WAITING_RETRY_MS);
    };
    const attempt = async () => {
      const result = await resolveAppSessionLaunch({ appType: 'svg-edit', search, role: 'student' });
      if (cancelled) return;
      if (result.mode === 'waiting') {
        setSessionWaiting(true);
        retryLater();
        return;
      }
      if (result.mode === 'error') {
        console.warn('[SVG-Edit] session resolve failed:', result.code, result.message);
        setSessionWaiting(true);
        retryLater();
        return;
      }
      if (result.mode !== 'app') {
        setSessionWaiting(false);
        return;
      }
      if (launch.incontroPin && result.pin === launch.incontroPin) {
        console.error('[SVG-Edit] il PIN risolto coincide col PIN incontro: auto-join rifiutato', result.pin);
        setSessionWaiting(false);
        return;
      }
      try {
        const joined = await bootstrapEmbeddedStudentSession(
          `?app_pin=${encodeURIComponent(result.pin)}&pseudonym_id=${encodeURIComponent(pseudonymId)}`,
          (data: {
            pin: string;
            session_id: string;
            participant_id: string;
            pseudonym_identity_id: string;
            avatar?: SessionJoinData['avatarConfig'];
          }): SessionJoinData => ({
            pin: data.pin,
            sessionId: data.session_id,
            participantId: data.participant_id,
            pseudonymIdentityId: data.pseudonym_identity_id,
            avatarConfig: data.avatar,
          })
        );
        if (cancelled || !joined) return;
        setSessionWaiting(false);
        setJoinData(joined);
        setView('editor');
        window.history.replaceState({}, '', withEmbeddedParam(window.location.pathname));
      } catch (error) {
        console.warn('[SVG-Edit] auto-join failed', error);
        // 4xx: la sessione non e' valida per questo studente, inutile insistere.
        if (error instanceof Error && error.message.startsWith('auto-join failed (4')) {
          setSessionWaiting(false);
          return;
        }
        retryLater();
      }
    };
    void attempt();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, []);

  // Il disegno fatto mentre la sessione non c'era non ha mai emesso un evento utile
  // dopo l'ingresso: appena arriva la sessione lo si salva.
  useEffect(() => {
    if (!joinData?.sessionId || !dirtyRef.current) return;
    if (deepLinkModeRef.current) return;
    scheduleSaveRef.current?.();
  }, [joinData]);

  // Dipende dall'id e non dall'oggetto utente: dopo il riscatto del ticket
  // refreshUser() gira due volte e produce due oggetti per lo stesso utente. Con
  // l'oggetto nelle dipendenze l'effetto ripartiva, azzerava teacherSessionId,
  // smontava #container mentre l'editor si avviava e il docente restava con
  // un'area vuota dentro techyweb (aula 2026-10-01, crash ext-markers/setAll).
  const teacherUserId = teacherUser?.id ?? null;
  useEffect(() => {
    if (!teacherPin || !teacherUserId || joinData) return;
    let cancelled = false;
    setTeacherSessionError(null);
    setTeacherSessionId(null);
    // Stesso stato di "Torna all'editor": pin e sessione azzerati, URL ripulito.
    const fallbackToLocal = (notice: string) => {
      setTeacherPin(null);
      setTeacherSessionId(null);
      setTeacherSessionError(null);
      setSessionNotice(notice);
      if (!isTl4kEmbedded()) {
        window.history.replaceState({}, '', withEmbeddedParam('/'));
      }
    };
    void (async () => {
      try {
        const br = await collabFetch(
          `${COLLAB_API_BASE}/sessions/by-pin/${encodeURIComponent(teacherPin)}`
        );
        if (!br.ok) {
          if (cancelled) return;
          // 404/410 (mancante, chiusa, scaduta) o altro errore: niente pagina bloccante,
          // si lavora in locale con un avviso non bloccante.
          if (isSessionGoneStatus(br.status)) {
            const reason = await readSessionGoneReason(br);
            if (!cancelled) fallbackToLocal(teacherSessionGoneNotice(reason));
          } else {
            fallbackToLocal(teacherSessionNetworkNotice());
          }
          return;
        }
        const s = (await br.json()) as { id: string; type?: string };
        if (s.type && s.type !== 'svg-edit') {
          if (!cancelled) setTeacherSessionError('Questo PIN non è una sessione SVG Edit.');
          return;
        }
        if (!cancelled) setTeacherSessionId(s.id);
      } catch {
        if (!cancelled) fallbackToLocal(teacherSessionNetworkNotice());
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [teacherPin, teacherUserId, joinData]);

  useEffect(() => {
    if (view === 'editor') return;
    setEditorInstance(null);
  }, [view]);

  const onSessionJoin = (data: SessionJoinData) => {
    setJoinData(data);
    setTeacherPin(null);
    setView('editor');
    if (collaboratePath()) {
      window.history.replaceState({}, '', '/');
    }
  };

  const toggleAppFullscreen = useCallback(async () => {
    const el = shellRef.current;
    if (!el) return;
    try {
      if (!document.fullscreenElement) {
        await el.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch {
      /* ignore */
    }
  }, []);

  const leaveStudentSession = () => {
    try {
      localStorage.removeItem(LS_KEY);
    } catch {
      /* ignore */
    }
    setJoinData(null);
    if (postTl4kGoBack()) {
      window.setTimeout(() => {
        window.location.href = withEmbeddedParam('/');
      }, 500);
      return;
    }
    window.location.href = '/';
  };

  useLayoutEffect(() => {
    if (view !== 'editor' || !containerEl || editorInstance) return;
    const el = containerEl;
    armedRef.current = false;
    Object.assign(el.style, { width: '100%', height: '100%', minHeight: '0' });
    const editor = new Editor(el);
    editor.setConfig({
      allowInitialUserOverride: true,
      extensions: [],
      noDefaultExtensions: false,
    });
    editor.init();
    setEditorInstance(editor);

    // Deep link: apri un artefatto specifico dal portfolio (?artifact_id=...)
    // senza sessione attiva. Senza JWT il server applica il percorso studente: serve il token
    // studente (studentFetch), altrimenti in enforce l'apertura risponde 404.
    const deepLinkId = new URLSearchParams(window.location.search).get('artifact_id');
    if (deepLinkId) {
      void (async () => {
        try {
          const res = await studentFetch(
            `${COLLAB_API_BASE.replace(/\/$/, '')}/artifacts/svg-edit/${encodeURIComponent(deepLinkId)}`
          );
          if (!res.ok) throw new Error(`GET /artifacts/svg-edit/:id -> ${res.status}`);
          const data = (await res.json()) as { svg_data?: string | null };
          if (data.svg_data) {
            suppressRef.current += 1;
            try {
              editor.loadSvgString(data.svg_data, { noAlert: true });
            } finally {
              suppressRef.current -= 1;
            }
          }
          const url = new URL(window.location.href);
          url.searchParams.delete('artifact_id');
          window.history.replaceState({}, '', `${url.pathname}${url.search}`);
        } catch (err) {
          console.warn('[SVG-Edit] deep-link artifact load failed:', err);
        }
      })();
    }

    if (typeof (window as any).XDOMAIN !== 'undefined' && (window as any).XDOMAIN) {
      editor.setConfig({ canvasName: 'xdomain', allowedOrigins: ['*'] });
    }

    let saveTimer: ReturnType<typeof setTimeout> | null = null;
    // NOTA: `canvasReady` non esiste sull'Editor (mai invocato da nessuno, da
    // sempre): l'autosave non partiva MAI. L'hook corretto e' `editor.ready(cb)`,
    // messo a disposizione da SVG-Edit proprio per eseguire codice a editor pronto
    // (o subito, se lo e' gia'). Vedi docs/bugs/svg-edit-autosave-canvasready.md.
    editor.ready(() => {
      const flushSave = () => {
        if (saveInFlightRef.current) return;
        const activeJoinData = joinDataRef.current;
        const activeTeacherSessionId = teacherSessionIdRef.current;
        const activeTeacherPin = teacherPinRef.current;
        const activeTeacherUser = teacherUserRef.current;
        const activeSessionId = activeJoinData?.sessionId ?? activeTeacherSessionId;
        if (!activeSessionId) return;
        const activePin = (activeJoinData?.pin ?? activeTeacherPin ?? '').trim().toUpperCase();
        if (!activePin) return;
        const isTeacherSave = Boolean(activeTeacherUser && activeTeacherPin && !activeJoinData);
        const saveType = artifactScopeFor({ isTeacher: isTeacherSave });
        const svgData = editor.svgCanvas.getSvgString();
        if (!svgData) return;
        const body: Record<string, unknown> = {
          svg_data: svgData,
          thumbnail_data_url: toSvgThumbnailDataUrl(svgData),
          mtime: Date.now(),
        };
        if (saveType === 'individual') {
          if (!activeJoinData?.pseudonymIdentityId) return;
          body.pseudonym_identity_id = activeJoinData.pseudonymIdentityId;
          body.pin = activePin;
        } else {
          body.pseudonym_identity_id = null;
        }
        pendingSaveRef.current = false;
        saveInFlightRef.current = true;
        void saveSessionArtifact({ app: 'svg-edit', sessionId: activeSessionId, scope: saveType, body })
          .then(() => {
            setSaveError(null);
          })
          .catch((err) => {
            // Nessun errore silenzioso: console + barra avvisi della pagina.
            console.error('[SVG-Edit] artifact save failed:', saveType, activeSessionId, err);
            setSaveError(artifactSaveUserMessage(err));
          })
          .finally(() => {
            saveInFlightRef.current = false;
            if (pendingSaveRef.current) {
              if (saveTimer) clearTimeout(saveTimer);
              saveTimer = setTimeout(flushSave, 400);
            }
          });
      };

      const scheduleSave = () => {
        pendingSaveRef.current = true;
        if (saveTimer) clearTimeout(saveTimer);
        saveTimer = setTimeout(() => {
          flushSave();
        }, 2000);
      };
      scheduleSaveRef.current = scheduleSave;

      // svgcanvas.bind() SOSTITUISCE il gestore precedente e lo restituisce:
      // sovrascrivere 'changed' spegneva Editor.elementChanged (pannello livelli,
      // pulsanti undo/redo, pannello contestuale, ext_elementChanged). Si concatena.
      // 'sourcechanged' (dialogo Sorgente, File>Apri via setSvgString) non emette
      // mai 'changed': va agganciato a parte.
      const chain = (eventName: 'changed' | 'sourcechanged') => {
        const previous = editor.svgCanvas.bind(eventName, (win: unknown, arg: unknown) => {
          if (typeof previous === 'function') previous(win, arg);
          if (!armedRef.current || suppressRef.current > 0 || deepLinkModeRef.current) return;
          dirtyRef.current = true;
          if (!pinRef.current) return;
          scheduleSave();
        });
      };
      chain('changed');
      chain('sourcechanged');
      // Armato dopo il giro corrente: le 'changed' dell'inizializzazione (documento
      // iniziale, livello di default) non sono lavoro dello studente.
      setTimeout(() => {
        armedRef.current = true;
      }, 0);
    });
  }, [view, editorInstance, containerEl]);

  if (view === 'join' && !isTeacher) {
    return (
      <div className={`${isEmbedded ? 'h-full' : 'min-h-screen'} bg-sky-50 ${isEmbedded ? 'p-3' : 'p-6'}`}>
        <button
          type="button"
          className="mb-4 min-h-11 rounded-lg border border-slate-300 bg-white px-3 text-slate-700"
          onClick={() => {
            window.history.replaceState({}, '', withEmbeddedParam('/'));
            setView('editor');
          }}
        >
          ← Torna all&apos;editor
        </button>
        <JoinSession onSessionJoin={onSessionJoin} />
      </div>
    );
  }

  if (teacherPin && !joinData && !authLoading && !teacherUser) {
    return (
      <div className={`mx-auto ${isEmbedded ? 'h-full' : 'min-h-screen'} max-w-md ${isEmbedded ? 'p-3' : 'p-6'}`}>
        <h1 className="mb-2 text-xl font-bold">SVG Edit — Insegnante</h1>
        <p className="mb-4 text-slate-600">PIN sessione: {teacherPin}</p>
        <AdminLogin
          onLoginSuccess={async () => {
            const u = await authService.getCurrentUser();
            setTeacherUser(u);
          }}
        />
      </div>
    );
  }

  if (teacherPin && !joinData && authLoading) {
    return (
      <div className={`flex ${isEmbedded ? 'h-full' : 'min-h-screen'} items-center justify-center ${isEmbedded ? 'p-3' : 'p-6'} text-slate-500`}>
        Caricamento…
      </div>
    );
  }

  if (teacherPin && !joinData && teacherUser && !teacherSessionId && !teacherSessionError) {
    return (
      <div className={`flex ${isEmbedded ? 'h-full' : 'min-h-screen'} items-center justify-center ${isEmbedded ? 'p-3' : 'p-6'} text-slate-500`}>
        Collegamento alla sessione…
      </div>
    );
  }

  if (teacherPin && !joinData && teacherUser && teacherSessionError) {
    return (
      <div className={`mx-auto ${isEmbedded ? 'h-full' : 'min-h-screen'} max-w-md ${isEmbedded ? 'p-3' : 'p-6'}`}>
        <p className="text-red-600">{teacherSessionError}</p>
        <button
          type="button"
          className="mt-4 rounded-lg border border-slate-300 bg-white px-4 py-2"
          onClick={() => {
            setTeacherPin(null);
            setTeacherSessionError(null);
            window.history.replaceState({}, '', '/');
          }}
        >
          Torna all&apos;editor
        </button>
      </div>
    );
  }

  const showChrome = !isEmbedded;

  const afterTitleSlot =
    isTeacher ? (
      <span style={{ fontSize: 14, color: 'var(--tl4k-neutral-600)' }}>PIN: {teacherPin}</span>
    ) : isStandaloneStudent && joinData ? (
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
        <Users size={20} aria-hidden className="text-slate-500" />
        {joinData.avatarConfig && <AvatarVisual config={joinData.avatarConfig} size="md" />}
        <span style={{ fontWeight: 600, color: 'var(--tl4k-neutral-800)' }}>
          {joinData.avatarConfig ? formatPseudonym(joinData.avatarConfig) : 'Studente'}
        </span>
        <button
          type="button"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            minHeight: 32,
            padding: '0 8px',
            borderRadius: 8,
            border: '1px solid #cbd5e1',
            background: 'var(--tl4k-surface-warm)',
            color: 'var(--tl4k-neutral-700)',
            fontWeight: 600,
            fontSize: 12,
            cursor: 'pointer',
          }}
          onClick={leaveStudentSession}
        >
          <LogOut size={14} aria-hidden />
          Esci dalla sessione
        </button>
      </div>
    ) : null;

  return (
    <div
      ref={shellRef}
      className={`${
        showChrome
          ? 'flex h-screen min-h-0 w-full flex-col'
          : 'relative flex h-full min-h-0 w-full flex-col overflow-hidden'
      } ${allCapsClassName(labelsUppercase) ?? ''}`}
    >
      {labelsUppercase && <AllCapsStyles />}
      {showChrome && (
        <TL4KAppHeader
          surface="neutral"
          appName="SVG Edit"
          standaloneSession={
            isStandalone
              ? {
                  badgeText: 'Senza sessione — salvataggio sul dispositivo',
                  onEnterSession: () => {
                    window.location.href = withEmbeddedParam('/collaborate');
                  },
                }
              : undefined
          }
          afterTitleSlot={afterTitleSlot}
          adminConsole={
            !joinData
              ? {
                  href: '/admin.html',
                  label: 'Area Admin',
                  openInNewTab: true,
                }
              : undefined
          }
          rightContent={
            <button
              type="button"
              aria-label={fullscreenActive ? 'Esci da schermo intero' : 'Schermo intero'}
              title={fullscreenActive ? 'Esci da schermo intero' : 'Schermo intero'}
              style={{
                minWidth: 40,
                minHeight: 40,
                borderRadius: 10,
                border: '1px solid #f39200',
                background: 'var(--tl4k-secondary-500)',
                color: 'var(--tl4k-surface-default)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
              onClick={() => void toggleAppFullscreen()}
            >
              {fullscreenActive ? <Minimize2 size={20} aria-hidden /> : <Maximize2 size={20} aria-hidden />}
            </button>
          }
          labelsUppercaseActive={labelsUppercase}
          onLabelsUppercaseToggle={() => setLabelsUppercase((v) => !v)}
          onAuthChange={refreshUser}
          onBackToApp={adminReturnTarget ? () => window.location.assign(adminReturnTarget.href) : undefined}
          backLabel={adminReturnTarget?.label ?? 'Indietro'}
        />
      )}

      {sessionWaiting && !joinData && <EmbeddedSessionWaitingBanner appLabel="SVG-Edit" />}

      {saveError && <EmbeddedSessionNoticeBar message={saveError} onDismiss={() => setSaveError(null)} />}

      {sessionNotice && !joinData && (
        <EmbeddedSessionNoticeBar message={sessionNotice} onDismiss={() => setSessionNotice(null)} />
      )}

      <div
        id="container"
        ref={setContainerEl}
        className={showChrome ? 'min-h-0 w-full flex-1' : 'min-h-0 w-full flex-1'}
      />

      {showChrome && (
        <footer
          className="flex h-9 shrink-0 items-center gap-2 border-t border-slate-200 bg-slate-50 px-2"
          style={{ zIndex: 50 }}
        >
          {pin && (
            <span className="font-mono text-xs font-bold text-orange-700">PIN {pin}</span>
          )}
          {joinData?.sessionName && (
            <span className="max-w-[200px] truncate text-xs text-slate-500">{joinData.sessionName}</span>
          )}
          <div className="flex-1" />
        </footer>
      )}
      {showChrome && <TL4KAppFooter appLabel="SVG Edit" />}
    </div>
  );
}
